import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Auth } from 'aws-amplify';
import {
  isOrphanOrgUser as computeIsOrphanOrgUser,
  isOrgOrProjectNameTooLong,
  isSupportedDatabaseType,
  orgOrProjectNameLengthMessage,
  Organization,
  PlatformRole,
  PostgresSettings,
  Project,
  User,
  UserOrganizationMembership,
} from '../types/onboarding';
import {
  addOrgUser as apiAddOrgUser,
  addProject as apiAddProject,
  addProjectUser as apiAddProjectUser,
  addUser as apiAddUser,
  getMe,
  listOrganizations,
  provisionInfrastructure as apiProvisionInfrastructure,
  removeOrgUser as apiRemoveOrgUser,
  switchActiveOrganization,
  updateProject as apiUpdateProject,
  updateUser as apiUpdateUser,
} from '../services/onboardingApi';
import {
  denormalizeRole,
  organizationFromBackend,
  userFromBackend,
  userMeFromBackend,
} from '../services/onboardingTransforms';
import { LoadingSpinner } from '../components/onboarding/LoadingIndicator';
import { Dialog } from '../components/onboarding/Dialog';
import {
  applyActiveOrganizationToUser,
  clearSessionActiveOrganizationId,
  resolveSessionActiveOrganizationId,
  setSessionActiveOrganizationId,
  WORKSPACE_INVALIDATED_EVENT,
  WorkspaceInvalidatedDetail,
} from '../utils/activeOrganizationStorage';
import { dispatch } from '../redux/store';
import { resetUserProjectScope } from '../redux/slice/userProjectSlice';
import { setShowNotification } from '../redux/slice/snackBarSlice';

interface AddUserInput {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  organization?: string;
  platformRole: PlatformRole;
}

interface AddOrgUserInput {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  organization: string;
  orgRole: PlatformRole;
}

interface AddProjectInput {
  name: string;
  customer: string;
  databaseType?: string;
  /** Required when databaseType is postgres. */
  postgres?: PostgresSettings;
  assignedUsers: string[];
  assignedUserEmails?: string[];
  startDate: Date;
  endDate?: Date;
}

interface UpdateUserInput {
  firstName?: string;
  lastName?: string;
  phone?: string;
  organization?: string;
  platformRole?: PlatformRole;
}

interface UpdateProjectInput {
  endDate?: Date;
  addUserEmail?: string;
  removeUserId?: string;
}

interface AuthContextType {
  currentUser: User | null;
  memberships: UserOrganizationMembership[];
  isOrphanOrgUser: boolean;
  orgContextVersion: number;
  isLoading: boolean;
  isSwitchingOrganization: boolean;
  error: string | null;
  users: User[];
  organizations: Organization[];
  projects: Project[];
  ensureOrganizations: () => Promise<Organization[]>;
  refresh: () => Promise<void>;
  switchOrganization: (organizationId: number) => Promise<void>;
  addOrganizationName: (name: string) => void;
  addUser: (input: AddUserInput) => Promise<User>;
  addOrgUser: (input: AddOrgUserInput) => Promise<User>;
  removeOrgUser: (input: { email: string; organization: string }) => Promise<void>;
  updateUser: (userId: string, updates: UpdateUserInput) => Promise<void>;
  addProject: (input: AddProjectInput) => Promise<Project>;
  updateProject: (projectId: string, updates: UpdateProjectInput) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const toIsoDate = (value: Date): string => {
  const yyyy = value.getFullYear();
  const mm = String(value.getMonth() + 1).padStart(2, '0');
  const dd = String(value.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const AUTH_CACHE_TTL_MS = 30_000;

interface AuthSnapshot {
  cognitoEmail: string;
  currentUser: User | null;
  memberships: UserOrganizationMembership[];
  organizationCatalog: Organization[];
  users: User[];
  projects: Project[];
  extraOrgNames: string[];
  error: string | null;
  timestamp: number;
}

let cachedSnapshot: AuthSnapshot | null = null;

type WorkspaceInvalidationContext = {
  priorOrganizationId: number | null;
  priorOrganizationName: string;
  priorMembershipCount: number;
};

function resolveOrganizationName(
  organizationId: number | null,
  memberships: UserOrganizationMembership[],
  fallbackUser: User | null
): string {
  if (organizationId == null) return '';
  const membership = memberships.find(
    (entry) => entry.organizationId === organizationId
  );
  if (membership) return membership.organizationName;
  if (fallbackUser?.activeOrganizationId === organizationId) {
    return fallbackUser.organization?.trim() ?? '';
  }
  return '';
}

function showWorkspaceInvalidationNotification(
  ctx: WorkspaceInvalidationContext,
  me: User,
  newMembershipCount: number
): void {
  if (me.isPlatformAdmin) return;

  const membershipLost = newMembershipCount < ctx.priorMembershipCount;
  const newOrgId = me.activeOrganizationId ?? null;
  const newName = me.organization?.trim() ?? '';
  const removedName = ctx.priorOrganizationName.trim();
  const orgSwitched =
    ctx.priorOrganizationId != null &&
    newOrgId != null &&
    ctx.priorOrganizationId !== newOrgId &&
    Boolean(removedName) &&
    Boolean(newName);

  if (newMembershipCount === 0) {
    if (ctx.priorMembershipCount > 0) {
      dispatch(
        setShowNotification(
          'Your organization access was removed. Contact an Admin to be invited again.'
        )
      );
    }
    return;
  }

  // Stale session repair or background refresh — no user-visible change.
  if (!membershipLost && !orgSwitched) {
    return;
  }

  if (orgSwitched && removedName !== newName) {
    dispatch(
      setShowNotification(
        `You no longer have access to ${removedName}. Switched to ${newName}.`
      )
    );
    return;
  }

  if (removedName && membershipLost) {
    dispatch(
      setShowNotification(`You no longer have access to ${removedName}.`)
    );
  }
}

const isSnapshotFresh = (snapshot: AuthSnapshot): boolean =>
  Date.now() - snapshot.timestamp < AUTH_CACHE_TTL_MS;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const initialSnapshot =
    cachedSnapshot && isSnapshotFresh(cachedSnapshot) ? cachedSnapshot : null;

  const [currentUser, setCurrentUser] = useState<User | null>(
    initialSnapshot?.currentUser ?? null
  );
  const [memberships, setMemberships] = useState<UserOrganizationMembership[]>(
    initialSnapshot?.memberships ?? []
  );
  const [users, setUsers] = useState<User[]>(initialSnapshot?.users ?? []);
  const [projects, setProjects] = useState<Project[]>(
    initialSnapshot?.projects ?? []
  );
  const [organizationCatalog, setOrganizationCatalog] = useState<Organization[]>(
    initialSnapshot?.organizationCatalog ?? []
  );
  const [extraOrgNames, setExtraOrgNames] = useState<string[]>(
    initialSnapshot?.extraOrgNames ?? []
  );
  const [orgContextVersion, setOrgContextVersion] = useState(0);
  const [isLoading, setIsLoading] = useState(initialSnapshot === null);
  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);
  const [error, setError] = useState<string | null>(
    initialSnapshot?.error ?? null
  );
  const orgsLoadPromiseRef = useRef<Promise<Organization[]> | null>(null);
  const workspaceInvalidationContextRef =
    useRef<WorkspaceInvalidationContext | null>(null);
  const workspaceRefreshInFlightRef = useRef(false);
  const skipWorkspaceInvalidationNotificationRef = useRef(false);
  const showAdminDemotionNotificationRef = useRef(false);
  const membershipsRef = useRef(memberships);
  const currentUserRef = useRef(currentUser);
  membershipsRef.current = memberships;
  currentUserRef.current = currentUser;

  const ensureOrganizations = useCallback(async (): Promise<Organization[]> => {
    if (organizationCatalog.length > 0 || extraOrgNames.length > 0) {
      const merged = new Map<string, Organization>();
      organizationCatalog.forEach((org) => merged.set(org.id, org));
      extraOrgNames.forEach((name) => {
        const exists = organizationCatalog.some(
          (org) => org.name.toLowerCase() === name.toLowerCase()
        );
        if (!exists) {
          merged.set(name, { id: name, name, createdAt: new Date() });
        }
      });
      return Array.from(merged.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    }
    if (orgsLoadPromiseRef.current) {
      return orgsLoadPromiseRef.current;
    }
    const promise = (async () => {
      try {
        const orgRes = await listOrganizations();
        const loaded = (orgRes.organizations ?? []).map(organizationFromBackend);
        setOrganizationCatalog(loaded);
        return loaded;
      } finally {
        orgsLoadPromiseRef.current = null;
      }
    })();
    orgsLoadPromiseRef.current = promise;
    return promise;
  }, [organizationCatalog, extraOrgNames]);

  const hydrate = useCallback(async (force = false) => {
    let cognitoEmail = '';
    try {
      const authUser = await Auth.currentAuthenticatedUser();
      const idTokenPayload =
        authUser?.signInUserSession?.idToken?.payload ?? {};
      cognitoEmail =
        authUser?.attributes?.email ??
        idTokenPayload.email ??
        idTokenPayload['cognito:username'] ??
        '';
    } catch {
      // not signed in
    }

    const normalizedEmail = cognitoEmail.trim().toLowerCase();

    if (
      !force &&
      cachedSnapshot &&
      cachedSnapshot.cognitoEmail === normalizedEmail &&
      isSnapshotFresh(cachedSnapshot)
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);
    cachedSnapshot = null;

    let me: User | null = null;
    let loadedMemberships: UserOrganizationMembership[] = [];
    let hydrateError: string | null = null;

    try {
      const meDto = await getMe();
      const mapped = userMeFromBackend(meDto);
      loadedMemberships = mapped.memberships;

      if (mapped.currentUser.isPlatformAdmin) {
        clearSessionActiveOrganizationId();
        me = mapped.currentUser;
      } else if (mapped.memberships.length === 0) {
        clearSessionActiveOrganizationId();
        me = mapped.currentUser;
      } else {
        const resolvedOrgId = resolveSessionActiveOrganizationId(
          mapped.memberships,
          mapped.currentUser.activeOrganizationId
        );
        if (resolvedOrgId != null) {
          setSessionActiveOrganizationId(resolvedOrgId);
        }
        me = applyActiveOrganizationToUser(
          mapped.currentUser,
          mapped.memberships,
          resolvedOrgId
        );
      }

      const wasPlatformAdmin =
        sessionStorage.getItem('rpt_is_platform_admin') === '1';
      if (wasPlatformAdmin && me && !me.isPlatformAdmin) {
        sessionStorage.removeItem('rpt_is_platform_admin');
        skipWorkspaceInvalidationNotificationRef.current = true;
        showAdminDemotionNotificationRef.current = true;
        workspaceInvalidationContextRef.current = null;
      } else if (me?.isPlatformAdmin) {
        sessionStorage.setItem('rpt_is_platform_admin', '1');
      } else {
        sessionStorage.removeItem('rpt_is_platform_admin');
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status !== 403 && status !== 401) {
        hydrateError = err?.displayMessage ?? 'Failed to load user context.';
      }
    }

    setUsers([]);
    setProjects([]);
    setMemberships(loadedMemberships);
    setOrganizationCatalog([]);
    setExtraOrgNames([]);
    setCurrentUser(me);
    setError(hydrateError);
    setIsLoading(false);

    const invalidationCtx = workspaceInvalidationContextRef.current;
    const skipWorkspaceNotification =
      skipWorkspaceInvalidationNotificationRef.current;
    const showAdminDemotion = showAdminDemotionNotificationRef.current;
    skipWorkspaceInvalidationNotificationRef.current = false;
    showAdminDemotionNotificationRef.current = false;
    workspaceInvalidationContextRef.current = null;

    if (showAdminDemotion && me) {
      dispatch(
        setShowNotification(
          'Your Admin access was removed. You now work within your organization memberships only.'
        )
      );
    } else if (invalidationCtx && me && !skipWorkspaceNotification) {
      showWorkspaceInvalidationNotification(
        invalidationCtx,
        me,
        loadedMemberships.length
      );
    }

    cachedSnapshot = {
      cognitoEmail: normalizedEmail,
      currentUser: me,
      memberships: loadedMemberships,
      organizationCatalog: [],
      users: [],
      projects: [],
      extraOrgNames: [],
      error: hydrateError,
      timestamp: Date.now(),
    };
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    if (isLoading) return;
    if (!cachedSnapshot) return;
    cachedSnapshot = {
      ...cachedSnapshot,
      currentUser,
      memberships,
      organizationCatalog,
      users,
      projects,
      extraOrgNames,
      error,
    };
  }, [isLoading, currentUser, memberships, organizationCatalog, users, projects, extraOrgNames, error]);

  const refresh = useCallback(async () => {
    await hydrate(true);
  }, [hydrate]);

  useEffect(() => {
    const onWorkspaceInvalidated = (event: Event) => {
      if (
        skipWorkspaceInvalidationNotificationRef.current ||
        showAdminDemotionNotificationRef.current
      ) {
        return;
      }
      if (!workspaceInvalidationContextRef.current) {
        const detail = (event as CustomEvent<WorkspaceInvalidatedDetail | null>)
          .detail;
        const removedOrgId = detail?.removedOrganizationId ?? null;
        workspaceInvalidationContextRef.current = {
          priorOrganizationId: removedOrgId,
          priorOrganizationName: resolveOrganizationName(
            removedOrgId,
            membershipsRef.current,
            currentUserRef.current
          ),
          priorMembershipCount: membershipsRef.current.length,
        };
      }
      if (workspaceRefreshInFlightRef.current) return;
      workspaceRefreshInFlightRef.current = true;
      void refresh()
        .then(() => {
          setOrgContextVersion((version) => version + 1);
        })
        .finally(() => {
          workspaceRefreshInFlightRef.current = false;
        });
    };
    window.addEventListener(WORKSPACE_INVALIDATED_EVENT, onWorkspaceInvalidated);
    return () => {
      window.removeEventListener(
        WORKSPACE_INVALIDATED_EVENT,
        onWorkspaceInvalidated
      );
    };
  }, [refresh]);

  const switchOrganization = useCallback(
    async (organizationId: number) => {
      setIsSwitchingOrganization(true);
      try {
        const meDto = await switchActiveOrganization(organizationId);
        setSessionActiveOrganizationId(organizationId);
        const mapped = userMeFromBackend(meDto);
        const user = applyActiveOrganizationToUser(
          mapped.currentUser,
          mapped.memberships,
          organizationId
        );
        setCurrentUser(user);
        setMemberships(mapped.memberships);
        cachedSnapshot = null;
        dispatch(resetUserProjectScope());
        setOrgContextVersion((version) => version + 1);
      } catch (err: any) {
        dispatch(
          setShowNotification(
            err?.displayMessage ??
              'Unable to switch organization. Please try again.'
          )
        );
        throw err;
      } finally {
        setIsSwitchingOrganization(false);
      }
    },
    []
  );

  const addOrganizationName = useCallback((name: string) => {
    const trimmed = name.trim();
    if (!trimmed || isOrgOrProjectNameTooLong(trimmed)) return;
    setExtraOrgNames((prev) =>
      prev.some((n) => n.toLowerCase() === trimmed.toLowerCase())
        ? prev
        : [...prev, trimmed]
    );
  }, []);

  const addUser = useCallback(
    async (input: AddUserInput): Promise<User> => {
      if (input.organization?.trim() && isOrgOrProjectNameTooLong(input.organization)) {
        throw new Error(orgOrProjectNameLengthMessage('Organization name'));
      }
      const response = await apiAddUser({
        first_name: input.firstName?.trim() || undefined,
        last_name: input.lastName?.trim() || undefined,
        email: input.email,
        ...(input.organization?.trim()
          ? { organisation_name: input.organization.trim() }
          : {}),
        org_role: denormalizeRole(input.platformRole),
        phone_no: input.phone?.trim() ? input.phone.trim() : undefined,
      });
      const created: User = {
        id: String(response.user_id),
        email: response.email ?? input.email,
        firstName: response.first_name ?? input.firstName ?? '',
        lastName: response.last_name ?? input.lastName ?? '',
        phone: response.phone_no ?? input.phone,
        organization: response.organisation_name ?? input.organization ?? '',
        platformRole: input.platformRole,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setUsers((prev) => [...prev, created]);
      if (created.organization) {
        addOrganizationName(created.organization);
      }
      await refresh();
      return created;
    },
    [addOrganizationName, refresh]
  );

  const addOrgUser = useCallback(
    async (input: AddOrgUserInput): Promise<User> => {
      const response = await apiAddOrgUser({
        email: input.email.trim().toLowerCase(),
        first_name: input.firstName?.trim() || undefined,
        last_name: input.lastName?.trim() || undefined,
        phone_no: input.phone?.trim() ? input.phone.trim() : undefined,
        organisation_name: input.organization,
        org_role: denormalizeRole(input.orgRole),
      });
      const created = userFromBackend(response.user);
      setUsers((prev) => {
        const exists = prev.some((u) => u.id === created.id);
        return exists
          ? prev.map((u) => (u.id === created.id ? created : u))
          : [...prev, created];
      });
      addOrganizationName(created.organization);
      await refresh();
      return created;
    },
    [addOrganizationName, refresh]
  );

  const removeOrgUser = useCallback(
    async (input: { email: string; organization: string }): Promise<void> => {
      await apiRemoveOrgUser({
        email: input.email.trim().toLowerCase(),
        organisation_name: input.organization,
      });
      cachedSnapshot = null;
      await refresh();
    },
    [refresh]
  );

  const updateUser = useCallback(
    async (userId: string, updates: UpdateUserInput): Promise<void> => {
      const numericId = Number(userId);
      if (Number.isNaN(numericId)) {
        throw new Error('Invalid user id.');
      }
      const response = await apiUpdateUser(numericId, {
        ...(updates.firstName !== undefined
          ? { first_name: updates.firstName.trim() || undefined }
          : {}),
        ...(updates.lastName !== undefined
          ? { last_name: updates.lastName.trim() || undefined }
          : {}),
        ...(updates.phone !== undefined ? { phone_no: updates.phone } : {}),
        ...(updates.organization
          ? { organisation_name: updates.organization }
          : {}),
        ...(updates.platformRole
          ? { org_role: denormalizeRole(updates.platformRole) }
          : {}),
      });
      const updated = userFromBackend(response.user);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? updated : u))
      );
      if (updates.organization) addOrganizationName(updates.organization);
      if (currentUser?.id === userId) {
        setCurrentUser((prev) =>
          prev
            ? {
                ...prev,
                firstName: updated.firstName,
                lastName: updated.lastName,
                phone: updated.phone,
                updatedAt: updated.updatedAt,
              }
            : prev
        );
      }
      cachedSnapshot = null;
      await refresh();
    },
    [addOrganizationName, refresh, currentUser?.id]
  );

  const addProject = useCallback(
    async (input: AddProjectInput): Promise<Project> => {
      if (isOrgOrProjectNameTooLong(input.name)) {
        throw new Error(orgOrProjectNameLengthMessage('Project name'));
      }
      if (isOrgOrProjectNameTooLong(input.customer)) {
        throw new Error(orgOrProjectNameLengthMessage('Organization name'));
      }
      const startIso = toIsoDate(input.startDate);
      const databaseType = isSupportedDatabaseType(input.databaseType)
        ? input.databaseType
        : undefined;
      if (databaseType === 'postgres' && !input.postgres) {
        throw new Error('PostgreSQL version is required');
      }
      const engineMajorVersion =
        databaseType === 'postgres' ? input.postgres?.engineMajorVersion : undefined;
      const createPayload: Parameters<typeof apiAddProject>[0] = {
        project_name: input.name,
        organization_name: input.customer,
        start_date: startIso,
        database_type: databaseType,
        engine_major_version: engineMajorVersion,
        schema_name: databaseType === 'postgres' ? input.postgres?.schemaName : undefined,
      };
      if (input.endDate) {
        createPayload.end_date = toIsoDate(input.endDate);
      }
      const createResponse = await apiAddProject(createPayload);
      const projectId = createResponse.project_id;

      for (let i = 0; i < input.assignedUsers.length; i++) {
        const assignedUserId = input.assignedUsers[i];
        const email =
          input.assignedUserEmails?.[i] ??
          users.find((u) => u.id === assignedUserId)?.email;
        if (!email) continue;
        await apiAddProjectUser({
          project_id: projectId,
          organisation_name: input.customer,
          email,
          start_date: startIso,
        });
      }

      if (databaseType) {
        try {
          await apiProvisionInfrastructure({
            project_id: projectId,
            organization_name: input.customer,
            project_name: input.name,
            database_type: databaseType,
            engine_major_version: engineMajorVersion,
          });
        } catch (err) {
          console.error(
            `Failed to kick off infrastructure provisioning for project ${projectId}`,
            err
          );
        }
      }

      const created: Project = {
        id: String(projectId),
        name: input.name,
        organization: input.customer,
        databaseType: input.databaseType,
        assignedUsers: input.assignedUsers,
        startDate: input.startDate,
        endDate: input.endDate,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setProjects((prev) => [...prev, created]);
      addOrganizationName(input.customer);
      await refresh();
      return created;
    },
    [addOrganizationName, refresh, users]
  );

  const updateProject = useCallback(
    async (projectId: string, updates: UpdateProjectInput): Promise<void> => {
      const numericId = Number(projectId);
      if (Number.isNaN(numericId)) {
        throw new Error('Invalid project id.');
      }
      if (updates.endDate) {
        await apiUpdateProject(numericId, {
          end_date: toIsoDate(updates.endDate),
        });
      } else if (updates.addUserEmail) {
        await apiUpdateProject(numericId, {
          add_user_email: updates.addUserEmail,
        });
      } else if (updates.removeUserId) {
        const remove = Number(updates.removeUserId);
        if (!Number.isNaN(remove)) {
          await apiUpdateProject(numericId, { remove_user_id: remove });
        }
      }
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? {
                ...p,
                endDate: updates.endDate ?? p.endDate,
                updatedAt: new Date(),
              }
            : p
        )
      );
      await refresh();
    },
    [refresh]
  );

  const organizations = useMemo(() => {
    const merged = new Map<string, Organization>();
    organizationCatalog.forEach((org) => merged.set(org.id, org));
    extraOrgNames.forEach((name) => {
      const exists = organizationCatalog.some(
        (org) => org.name.toLowerCase() === name.toLowerCase()
      );
      if (!exists) {
        merged.set(name, { id: name, name, createdAt: new Date() });
      }
    });
    return Array.from(merged.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [organizationCatalog, extraOrgNames]);

  const isOrphanOrgUser = useMemo(
    () => computeIsOrphanOrgUser(currentUser, memberships),
    [currentUser, memberships]
  );

  const value = useMemo<AuthContextType>(
    () => ({
      currentUser,
      memberships,
      isOrphanOrgUser,
      orgContextVersion,
      isLoading,
      isSwitchingOrganization,
      error,
      users,
      organizations,
      projects,
      ensureOrganizations,
      refresh,
      switchOrganization,
      addOrganizationName,
      addUser,
      addOrgUser,
      removeOrgUser,
      updateUser,
      addProject,
      updateProject,
    }),
    [
      currentUser,
      memberships,
      isOrphanOrgUser,
      orgContextVersion,
      isLoading,
      isSwitchingOrganization,
      error,
      users,
      organizations,
      projects,
      ensureOrganizations,
      refresh,
      switchOrganization,
      addOrganizationName,
      addUser,
      addOrgUser,
      removeOrgUser,
      updateUser,
      addProject,
      updateProject,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <Dialog
        open={isSwitchingOrganization}
        onClose={() => {}}
        labelledBy="org-switch-loading-title"
        panelClassName="max-w-xs text-center"
        backdropClassName="z-[1400]"
      >
        <div className="flex flex-col items-center gap-3 py-2">
          <LoadingSpinner size="xl" className="text-brand" />
          <p
            id="org-switch-loading-title"
            className="text-sm font-medium text-gray-900"
          >
            Switching organization...
          </p>
        </div>
      </Dialog>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
